import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { getOrderBetween } from '@epam/uui-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createTaskAndReview, updateTaskAndReview } from 'api/task.service';
import { useToast } from 'hooks/ui';
import { Task } from 'models/task.model';
import { setDraftTask, useTaskStore } from 'store/task';
import { useApplicationStore } from 'store/application';
import { taskRoutes } from 'router/taskRoutes';
import { taskKeys } from './queryKeys';

export const useTaskConditionalReviewQuery = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { assignedGroup, tasks } = useTaskStore((state) => state);
  const { setLoading, removeLoading } = useApplicationStore((state) => state);

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: { task: Task; new: boolean }) => {
      setLoading();

      if (payload.new) {
        return createTaskAndReview(
          {
            title: payload.task.title,
            description: payload.task.description,
            errorMessage: payload.task.errorMessage,
            successMessage: payload.task.successMessage,
            comment: payload.task.comment,
            validator: payload.task.validator,
            workModels: payload.task.workModels,
            answers: payload.task.answers,
            taskGroupId: assignedGroup,
            order: getOrderBetween(tasks[tasks.length - 1]?.order, null),
          },
          payload.task.attachments,
        );
      }

      return updateTaskAndReview({
        id: payload.task.id,
        title: payload.task.title,
        description: payload.task.description,
        errorMessage: payload.task.errorMessage,
        successMessage: payload.task.successMessage,
        comment: payload.task.comment,
        validator: payload.task.validator,
        workModels: payload.task.workModels,
        answers: payload.task.answers,
        taskGroupId: assignedGroup,
        order: payload.task.order,
        attachments: payload.task.attachments,
      });
    },
    onSuccess: (task) => {
      toast.success('Task has been sent for review successfully');
      navigate(taskRoutes.taskId(task.id));
      setDraftTask({ task });
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      removeLoading();
    },
  });

  return { isLoading: isPending, mutate };
};
